import type { AdminCustomerCart } from "@hobbyshopify/backend-client";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { loadCarts } from "../../core/ops/cart-ops";
import { Loader } from "../../core/primitives/loader";
import { shopEntryId } from "../../core/primitives/workspace";
import { CustomerCartController } from "../../sections/customer-cart/customer-cart-controller";
import { CustomerCartsActions, CustomerCartsState } from "./customer-carts-model";

export type CustomerCartsDeps = Pick<
  AppDeps,
  "client" | "authHandler" | "storeRepository" | "providersRepository" | "pendingCartsRepository" | "stripeTerminal"
>;

/**
 * The carts customers built at this shop: what the merchant edits, deletes or
 * takes payment for at the counter. Every write reloads the list rather than
 * patching it, since the server decides what an edit did to the checkout.
 */
export class CustomerCartsController extends SubtreeController implements CustomerCartsActions {
  readonly state: CustomerCartsState;
  readonly cart: CustomerCartController;
  readonly #list: Loader<string, AdminCustomerCart[]>;
  #shopId: string | null = null;
  /** The newest read of the carts. */
  #read: Promise<void> = Promise.resolve();

  constructor(
    readonly deps: CustomerCartsDeps,
    customerId: string | null = null,
  ) {
    super();
    this.state = new CustomerCartsState(customerId);
    this.subtree.put(CustomerCartsState, this.state);
    this.subtree.put(CustomerCartsActions, this);
    this.#list = this.own(
      new Loader({
        status: this.state.status,
        fetch: (shopId: string) => loadCarts({ ...deps, shopId, locationId: deps.storeRepository.locationId }),
        loaded: (carts) => {
          this.state.carts.value = carts;
          this.cart.cartRead();
        },
      }),
    );
    this.sync(() => {
      const { shopId, locationId } = deps.storeRepository;
      this.state.openCarts.value =
        shopId === null ? 0 : deps.pendingCartsRepository.countFor(shopEntryId(shopId, locationId));
    }, [deps.pendingCartsRepository, deps.storeRepository]);
    this.cart = this.own(
      new CustomerCartController(this.subtree, deps, {
        reload: () => (this.#shopId === null ? Promise.resolve(false) : this.#readAgain(this.#shopId)),
      }),
    );
    this.sync(() => {
      const shopId = deps.storeRepository.shopId;
      this.state.shopId.value = shopId;
      if (shopId === this.#shopId) return;
      this.#shopId = shopId;
      // The page keeps the carts it shows through a read: another shop's are not those.
      this.state.carts.value = [];
      if (shopId === null) {
        this.#list.reset();
        return;
      }
      void this.#load(shopId);
    }, [deps.storeRepository]);
  }

  reload = () => {
    if (this.#shopId !== null) void this.#load(this.#shopId);
  };

  #load(shopId: string): Promise<void> {
    this.#read = this.#list.load(shopId);
    return this.#read;
  }

  /** Whether the carts could be read: answered once the newest read has, when a newer one overtakes this one. */
  async #readAgain(shopId: string): Promise<boolean> {
    let read = this.#load(shopId);
    for (;;) {
      await read.catch(() => undefined);
      if (read === this.#read) return this.state.status.value.kind === "loaded";
      read = this.#read;
    }
  }
}
